import React, {useState, useEffect } from 'react';
import {db, storage } from '../../firebase'; // Ensure these are correctly imported from your firebase.ts
import {collection, addDoc, query, orderBy, onSnapshot, serverTimestamp, Timestamp } from 'firebase/firestore';
import {ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import {getAuth } from 'firebase/auth'; // To get the current user's UID

import {
  PageContainer,
  SectionTitle,
  UploadForm,
  FormGroup,
  Label,
  Input,
  TextArea,
  Button,
  UploadStatus,
  ErrorMessage,
  LoadingMessage,
  PostsGrid,
  PostCard,
  VideoPlayer,
  ImageCarousel,
  PostContent,
  PostDescription,
  PostMeta,
  ProgressBarContainer,
  ProgressBar,
  Spinner,
  NoPostsMessage
} from './PostUploadPage.styles';

// Interface for a post stored in Firestore
interface Post {
  id: string;
  videoUrl: string;
  imageUrls: string[]; // Changed to an array for multiple images
  description: string;
  createdAt: Timestamp;
  userId: string;
}

const PostUploadPage: React.FC = () => {
  const auth = getAuth(); // Get Firebase Auth instance
  const currentUser = auth.currentUser;

  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [imageFile1, setImageFile1] = useState<File | null>(null);
  const [imageFile2, setImageFile2] = useState<File | null>(null);
  const [description, setDescription] = useState<string>('');

  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadMessage, setUploadMessage] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const [posts, setPosts] = useState<Post[]>([]);
  const [fetchingPosts, setFetchingPosts] = useState<boolean>(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    setFetchingPosts(true);
    setFetchError(null);

    // Query for posts, ordered by creation date (newest first)
    const postsCollectionRef = collection(db, 'posts');
    const q = query(postsCollectionRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q,
      (snapshot) => {
        const fetchedPosts: Post[] = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data() as Omit<Post, 'id'> // Cast data, id is handled separately
        }));
        setPosts(fetchedPosts);
        setFetchingPosts(false);
      },
      (error) => {
        console.error("Error fetching posts:", error);
        setFetchError(`Failed to load posts: ${error.message}`);
        setFetchingPosts(false);
      }
    );

    // Cleanup subscription on component unmount
    return () => unsubscribe();
  }, []); // Empty dependency array means this runs once on mount

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, setter: React.Dispatch<React.SetStateAction<File | null>>) => {
    if (e.target.files && e.target.files[0]) {
      setter(e.target.files[0]);
    } else {
      setter(null);
    }
  };

  const uploadFile = async (file: File, path: string, onProgress?: (progress: number) => void): Promise<string> => {
    const fileRef = ref(storage, path);
    const uploadTask = uploadBytesResumable(fileRef, file);

    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          if (onProgress) onProgress(progress);
        },
        (error) => {
          console.error("Upload failed:", error);
          reject(`Upload failed: ${error.message}`);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(downloadURL);
          } catch (error: any) {
            reject(`Failed to get download URL: ${error.message}`);
          }
        }
      );
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentUser) {
      setUploadError("You must be logged in to upload a post.");
      return;
    }

    if (!videoFile || !imageFile1 || !imageFile2 || !description.trim()) {
      setUploadError("Please select a video, two images, and provide a description.");
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadMessage("Starting upload...");
    setUploadError(null);

    const userId = currentUser.uid;
    const timestamp = Date.now();
    let videoUrl = '';
    const imageUrls: string[] = [];

    try {
      // 1. Upload Video
      setUploadMessage("Uploading video...");
      videoUrl = await uploadFile(videoFile, `posts/${userId}/videos/${timestamp}_${videoFile.name}`, (progress) => {
        setUploadProgress(progress / 3); // Video is 1/3 of total progress
      });

      // 2. Upload Images concurrently
      setUploadMessage("Uploading images...");
      const imageUploadPromises = [
        uploadFile(imageFile1, `posts/${userId}/images/${timestamp}_${imageFile1.name}`, (progress) => {
          setUploadProgress(33.33 + progress / 3); // Image 1 is 1/3
        }),
        uploadFile(imageFile2, `posts/${userId}/images/${timestamp}_${imageFile2.name}`, (progress) => {
          setUploadProgress(66.66 + progress / 3); // Image 2 is 1/3
        })
      ];

      const uploadedImageUrls = await Promise.all(imageUploadPromises);
      imageUrls.push(...uploadedImageUrls);
      
      setUploadProgress(100);
      setUploadMessage("Files uploaded successfully. Saving post data...");

      // 3. Save post data to Firestore
      await addDoc(collection(db, 'posts'), {
        userId,
        videoUrl,
        imageUrls,
        description: description.trim(),
        createdAt: serverTimestamp() // Use server timestamp for consistency
      });

      setUploadMessage("Post created successfully!");
      
      // Reset form fields after successful upload
      setVideoFile(null);
      setImageFile1(null);
      setImageFile2(null);
      setDescription('');
      // Clear file inputs visually
      const videoInput = document.getElementById('videoFile') as HTMLInputElement;
      if (videoInput) videoInput.value = '';
      const image1Input = document.getElementById('imageFile1') as HTMLInputElement;
      if (image1Input) image1Input.value = '';
      const image2Input = document.getElementById('imageFile2') as HTMLInputElement;
      if (image2Input) image2Input.value = '';

    } catch (error: any) {
      console.error("Error creating post:", error);
      setUploadError(`Failed to create post: ${error.message}`);
      setUploadMessage(null); // Clear success message on error
    } finally {
      setUploading(false);
      // setUploadProgress(0); // Optionally reset progress bar after completion/error
    }
  };

  return (
    <PageContainer>
      <SectionTitle>Create New Post</SectionTitle>

      {/* Upload Form */}
      <UploadForm onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="videoFile">Upload Video:</Label>
          <Input
            type="file"
            id="videoFile"
            accept="video/*"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFileChange(e, setVideoFile)}
            disabled={uploading}
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="imageFile1">Upload Image 1:</Label>
          <Input
            type="file"
            id="imageFile1"
            accept="image/*"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFileChange(e, setImageFile1)}
            disabled={uploading}
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="imageFile2">Upload Image 2:</Label>
          <Input
            type="file"
            id="imageFile2"
            accept="image/*"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFileChange(e, setImageFile2)}
            disabled={uploading}
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="description">Description:</Label>
          <TextArea
            id="description"
            value={description}
            onChange={(e: {target: {value: React.SetStateAction<string>; }; }) => setDescription(e.target.value)}
            rows={4}
            placeholder="Enter a description for your post..."
            disabled={uploading}
          />
        </FormGroup>

        <Button type="submit" disabled={uploading || !currentUser}>
          {uploading ? 'Uploading...' : 'Create Post'}
        </Button>

        {uploading && (
          <ProgressBarContainer>
            <ProgressBar progress={uploadProgress} />
          </ProgressBarContainer>
        )}
        {uploadMessage && <UploadStatus color={uploadError ? '#dc3545' : '#28a745'}>{uploadMessage}</UploadStatus>}
        {uploadError && <ErrorMessage>{uploadError}</ErrorMessage>}
        {!currentUser && <ErrorMessage>Please log in to create a post.</ErrorMessage>}
      </UploadForm>

      <SectionTitle>All Posts</SectionTitle>

      {/* Display Posts */}
      {fetchingPosts && (
        <LoadingMessage>
          <Spinner />
          Loading posts...
        </LoadingMessage>
      )}
      {fetchError && <ErrorMessage>{fetchError}</ErrorMessage>}
      {!fetchingPosts && posts.length === 0 && <NoPostsMessage>No posts available yet. Be the first to upload!</NoPostsMessage>}
      
      <PostsGrid>
        {!fetchingPosts && posts.map((post) => (
          <PostCard key={post.id}>
            <VideoPlayer controls src={post.videoUrl} preload="metadata">
              Your browser does not support the video tag.
            </VideoPlayer>
            {post.imageUrls && post.imageUrls.length > 0 && (
              <ImageCarousel>
                {post.imageUrls.map((imageUrl, index) => (
                  <img key={index} src={imageUrl} alt={`Post image ${index + 1}`} />
                ))}
              </ImageCarousel>
            )}
            <PostContent>
              <PostDescription>{post.description}</PostDescription>
              <PostMeta>
                Posted by: {post.userId.substring(0, 6)}... <br />
                On: {post.createdAt instanceof Timestamp ? post.createdAt.toDate().toLocaleString() : 'N/A'}
              </PostMeta>
            </PostContent>
          </PostCard>
        ))}
      </PostsGrid>
    </PageContainer>
  );
};

export default PostUploadPage;